/**
 * Tiny event bus between the chat, the onboarding and the on-screen guide (the mascot leaving the chat to point at a
 * real button). Everything is window events so no provider is needed.
 */
export interface GuideRequest {
  /** Value of the data-guide attribute of the element to point at. */
  anchor: string;
  /** Page to open first when the anchor is not on the current screen (optional). */
  path?: string;
  /** Label of the button, for the speech bubble. */
  title: string;
  /** Anchor to use when the first one is not visible (top menu keeps items inside group dropdowns). */
  fallbackAnchor?: string;
}

export const GUIDE_REQUEST_EVENT = "fillsign:guide";
export const GUIDE_STATE_EVENT = "fillsign:guide-state";   // detail: { active: boolean; rect?: DOMRect }
export const MASCOT_JUMP_EVENT = "fillsign:mascot-jump";   // detail: { from: DOMRect }
export const MASCOT_LANDED_EVENT = "fillsign:mascot-landed";
export const ASSISTANT_ASK_EVENT = "fillsign:assistant-ask";   // detail: { message: string }

/** Opens the chat and sends a message on the person's behalf (buttons like "Pedir ajuda ao FillSign"). */
export function askAssistant(message: string) {
  window.dispatchEvent(new CustomEvent(ASSISTANT_ASK_EVENT, { detail: { message } }));
}

export function requestGuide(req: GuideRequest) {
  window.dispatchEvent(new CustomEvent<GuideRequest>(GUIDE_REQUEST_EVENT, { detail: req }));
}

/** Makes the mascot jump from `from` into the chat launcher (used when the onboarding closes). */
export function mascotJumpToChat(from: DOMRect) {
  window.dispatchEvent(new CustomEvent(MASCOT_JUMP_EVENT, { detail: { from } }));
}

/** Where the mascot lives on the page: the chat header when open, the launcher otherwise. */
export function mascotHomeRect(): DOMRect | null {
  const el = document.querySelector<HTMLElement>("[data-guide-home]");
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 ? r : null;
}

/** App routes the assistant may open → what to point at, and where that element lives. */
export function guideForPath(path: string, title: string): GuideRequest | null {
  const clean = path.split("?")[0];
  if (clean === "/documents/new") return { anchor: "new-document", path: "/documents", title };
  // Sidebar item anchors are nav-<route>; the top menu shows the group button instead.
  const groups: Record<string, string> = {
    "/dashboard": "work", "/documents": "work", "/modelos": "work", "/signatures": "work",
    "/contacts": "records", "/leads": "records", "/document-templates": "records",
    "/audit-logs": "admin", "/settings": "system", "/help": "system",
  };
  const group = groups[clean];
  if (!group) return null;
  return { anchor: `nav-${clean.replace("/", "")}`, fallbackAnchor: `nav-group-${group}`, title };
}

/** The first visible element among the request's anchors. */
export function findGuideTarget(req: GuideRequest): HTMLElement | null {
  for (const anchor of [req.anchor, req.fallbackAnchor]) {
    if (!anchor) continue;
    const el = document.querySelector(`[data-guide="${anchor}"]`);
    if (isVisible(el)) return el;
  }
  return null;
}

export function isVisible(el: Element | null): el is HTMLElement {
  if (!el) return false;
  const r = (el as HTMLElement).getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return false;
  const style = window.getComputedStyle(el as HTMLElement);
  return style.visibility !== "hidden" && style.display !== "none";
}
